import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, CalendarDays, Search, LayoutDashboard } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { label: "Services", href: "#services" },
  { label: "Referrals", href: "#referrals" },
  { label: "Gallery", href: "#gallery" },
  { label: "Portfolio", href: "/portfolio", isRoute: true },
  { label: "FAQ", href: "/faq", isRoute: true },
  { label: "About", href: "#about" },
  { label: "Get a Quote", href: "#quote" },
];

export const Navigation = ({ scrollY }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const isScrolled = scrollY > 50;
  const location = useLocation();
  const navigate = useNavigate();
  const isLanding = location.pathname === "/";

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleNavClick = (href, isRoute = false) => {
    setMobileOpen(false);
    if (isRoute) {
      navigate(href);
    } else if (isLanding) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/" + href);
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (isLanding) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  return (
    <>
      <motion.nav
        data-testid="navigation"
        style={{ top: "var(--banner-h, 0px)" }}
        className={`fixed left-0 right-0 z-40 transition-colors duration-300 ${
          isScrolled ? "bg-[#050505]/95 backdrop-blur-md border-b border-white/5" : "bg-transparent"
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between h-20">
          <a href="/" onClick={handleLogoClick} data-testid="nav-logo" className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_auto-paint-brisbane/artifacts/37p3il2l_Screenshot%202025-04-27%20024102.png"
              alt="Velocity Paint and Panel"
              className="h-10 w-auto"
            />
            <div className="hidden sm:block">
              <span className="font-heading text-xl tracking-[0.2em] uppercase text-white leading-none">Velocity</span>
              <span className="block text-[10px] tracking-[0.3em] uppercase text-white font-body">Paint & Panel</span>
            </div>
          </a>

          <div className="hidden md:flex items-center gap-6">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavClick(item.href, item.isRoute)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`nav-link text-sm tracking-[0.15em] uppercase hover:text-[#3b82f6] transition-colors font-body font-medium bg-transparent border-none cursor-pointer ${
                  isScrolled ? "text-zinc-400" : "text-white/90"
                }`}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => navigate("/track")}
              data-testid="nav-track-btn"
              className={`flex items-center gap-2 text-sm tracking-[0.15em] uppercase hover:text-[#3b82f6] transition-colors font-body font-medium bg-transparent border-none cursor-pointer ${
                isScrolled ? "text-zinc-400" : "text-white/90"
              }`}
            >
              <Search size={14} />
              Track
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              data-testid="nav-dashboard-btn"
              className={`flex items-center gap-2 text-sm tracking-[0.15em] uppercase hover:text-zinc-300 transition-colors font-body font-medium bg-transparent border-none cursor-pointer ${
                isScrolled ? "text-zinc-500" : "text-white/60"
              }`}
            >
              <LayoutDashboard size={14} />
              Admin
            </button>
            <button
              onClick={() => navigate("/book")}
              data-testid="nav-book-btn"
              className="flex items-center gap-2 border border-[#3b82f6]/30 text-[#3b82f6] font-heading text-sm tracking-[0.15em] uppercase px-4 py-2.5 hover:bg-[#3b82f6]/10 transition-colors cursor-pointer bg-transparent"
            >
              <CalendarDays size={14} />
              Book
            </button>
            <a
              href="sms:0450645926"
              data-testid="nav-call-btn"
              className="flex items-center gap-2 bg-white text-black font-heading text-sm tracking-[0.15em] uppercase px-5 py-2.5 hover:bg-zinc-200 transition-colors no-underline"
            >
              <Phone size={14} />
              Text Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white bg-transparent border-none cursor-pointer p-2"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      <AnimatePresence mode="wait">
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-50 bg-[#050505]/98 backdrop-blur-xl flex flex-col items-center justify-center gap-6 pt-20"
          >
            {/* Close button in top right */}
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-6 right-6 text-white bg-transparent border-none cursor-pointer p-2"
              data-testid="mobile-menu-close"
            >
              <X size={28} />
            </button>
            
            {NAV_ITEMS.map((item, i) => (
              <motion.button
                key={item.label}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ delay: i * 0.05, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => handleNavClick(item.href, item.isRoute)}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-heading text-2xl sm:text-3xl tracking-[0.2em] uppercase text-white hover:text-zinc-400 transition-colors bg-transparent border-none cursor-pointer"
              >
                {item.label}
              </motion.button>
            ))}
            <motion.button
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ delay: 0.25, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => { setMobileOpen(false); navigate("/track"); }}
              data-testid="mobile-nav-track"
              className="font-heading text-2xl sm:text-3xl tracking-[0.2em] uppercase text-zinc-400 hover:text-white transition-colors bg-transparent border-none cursor-pointer flex items-center gap-3"
            >
              <Search size={24} />
              Track Job
            </motion.button>
            <motion.button
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ delay: 0.30, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => { setMobileOpen(false); navigate("/book"); }}
              data-testid="mobile-nav-book"
              className="font-heading text-2xl sm:text-3xl tracking-[0.2em] uppercase text-[#3b82f6] hover:text-blue-300 transition-colors bg-transparent border-none cursor-pointer"
            >
              Book Now
            </motion.button>
            <motion.button
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ delay: 0.35, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => { setMobileOpen(false); navigate("/dashboard"); }}
              data-testid="mobile-nav-dashboard"
              className="font-heading text-lg sm:text-xl tracking-[0.2em] uppercase text-zinc-600 hover:text-zinc-400 transition-colors bg-transparent border-none cursor-pointer flex items-center gap-2"
            >
              <LayoutDashboard size={18} />
              Admin
            </motion.button>
            <motion.a
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ delay: 0.40, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              href="sms:0450645926"
              data-testid="mobile-call-btn"
              className="flex items-center gap-3 bg-white text-black font-heading text-lg sm:text-xl tracking-[0.15em] uppercase px-6 py-3 mt-2 no-underline"
            >
              <Phone size={18} />
              0450 645 926
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
